import axios from 'axios';

// Generate or retrieve a persistent client ID per browser
function getClientId() {
  const KEY = 'chat_client_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    // Simple UUID v4 generator
    id = ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
    localStorage.setItem(KEY, id);
  }
  return id;
}

// All API calls go through the Vite proxy (dev) or nginx proxy (prod) at /api.
// The backend URL is never embedded in the client bundle — it only lives in
// vite.config.js (dev) and nginx.conf (prod), both of which strip the /api prefix
// before forwarding to the backend container.
const API_BASE_URL = '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,  // 60s default — large uploads use per-request overrides
});

// Attach client ID header to all requests
apiClient.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers['X-Client-Id'] = getClientId();
  // Attach JWT bearer token if present
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export const chatApi = {
  // Streaming chat
  async streamChat(message, conversationHistory = [], campusAffiliation = null) {
  const response = await fetch(`${API_BASE_URL}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Id': getClientId(),
      },
      body: JSON.stringify({
        message,
        conversation_history: conversationHistory,
        use_context: true,
        campus_affiliation: campusAffiliation
      })
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return response;
  },

  // Stop streaming
  async stopChat() {
    return apiClient.post('/chat/stop');
  }
};

export const chatSessionApi = {
  // Get all chat sessions
  async getSessions() {
  const response = await apiClient.get('/chat/sessions');
    return response.data;
  },

  // Create new session
  async createSession(title = 'New Chat') {
  const response = await apiClient.post('/chat/sessions', { title });
    return response.data;
  },

  // Get specific session
  async getSession(sessionId) {
  const response = await apiClient.get(`/chat/sessions/${sessionId}`);
    return response.data;
  },

  // Update session
  async updateSession(sessionId, updates) {
  const response = await apiClient.put(`/chat/sessions/${sessionId}`, updates);
    return response.data;
  },

  // Delete session
  async deleteSession(sessionId) {
  const response = await apiClient.delete(`/chat/sessions/${sessionId}`);
    return response.data;
  },

  // Add message to session
  async addMessage(sessionId, role, content, sources = null, retrieval_confidence = null, generation_confidence = null) {
  const response = await apiClient.post(`/chat/sessions/${sessionId}/add_message`, {
      role,
      content,
      sources,
      retrieval_confidence,
      generation_confidence
    });
    return response.data;
  }
};

export const pdfApi = {
  // Search PDFs
  async searchPDFs(query, campusAffiliation = null) {
  const response = await apiClient.post('/search', { query, campus_affiliation: campusAffiliation });
    return response.data;
  },

  // Get all PDFs
  async getPDFs() {
    const response = await apiClient.get('/pdfs');
    return response.data;
  },

  // Upload PDF
  async uploadPDF(file, onUploadProgress) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/pdfs/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 3600000,  // 1 hour — large files can take a while
      onUploadProgress,
    });
    return response.data;
  },

  // Delete PDF
  async deletePDF(filename) {
    const response = await apiClient.delete(`/pdfs/${encodeURIComponent(filename)}`);
    return response.data;
  },

  // View PDF
  getPDFUrl(filename) {
    return `${API_BASE_URL}/pdfs/${encodeURIComponent(filename)}/view`;
  },

  // Get OCR Preview for a specific page
  async getOCRPreview(filename, page = 1) {
    const response = await apiClient.get(`/pdfs/${encodeURIComponent(filename)}/ocr-preview`, {
      params: { page },
      timeout: 60000 // OCR can take longer
    });
    return response.data;
  }
};

export const vectorStoreApi = {
  // Rebuild vector store
  async rebuild() {
    const response = await apiClient.post('/vectorstore/rebuild');
    return response.data;
  },

  // Get vector store status
  async getStatus() {
    const response = await apiClient.get('/vectorstore/status');
    return response.data;
  },

  // Test vector store
  async test() {
    const response = await apiClient.get('/vectorstore/test');
    return response.data;
  },

  // Get vectorstore rebuild schedule
  async getSchedule() {
    const response = await apiClient.get('/vectorstore/schedule');
    return response.data;
  },

  // Update vectorstore rebuild schedule
  async updateSchedule(schedule) {
    const response = await apiClient.post('/vectorstore/schedule', schedule);
    return response.data;
  }
};

export const modelApi = {
  // Get current config
  async getConfig() {
    const response = await apiClient.get('/model/config');
    return response.data;
  },

  // Update config
  async updateConfig(config) {
    const response = await apiClient.post('/model/config', config);
    return response.data;
  },

  // Reset config
  async resetConfig() {
    const response = await apiClient.post('/model/reset');
    return response.data;
  },

  // Get available LLM models
  async getAvailableModels() {
    const response = await apiClient.get('/model/available', { params: { type: 'llm' } });
    return response.data;
  },

  // Get available embedding models
  async getAvailableEmbeddingModels() {
    const response = await apiClient.get('/model/available', { params: { type: 'embedding' } });
    return response.data;
  }
};

export const knowledgeGraphApi = {
  // Get graph statistics
  async getStats() {
    const response = await apiClient.get('/knowledge-graph/stats');
    return response.data;
  },

  // Get full graph or filtered
  async getGraph(filters = {}) {
    const params = new URLSearchParams();
    if (filters.policy_id) params.append('policy_id', filters.policy_id);
    if (filters.category) params.append('category', filters.category);
    if (filters.max_depth) params.append('max_depth', filters.max_depth);
    if (filters.max_nodes) params.append('max_nodes', filters.max_nodes);
    if (filters.max_edges) params.append('max_edges', filters.max_edges);
    
    const response = await apiClient.get(`/knowledge-graph?${params.toString()}`);
    return response.data;
  },

  // Get list of categories
  async getCategories() {
    const response = await apiClient.get('/knowledge-graph/categories');
    return response.data;
  },

  // Build or rebuild graph (development only)
  async buildGraph(clearExisting = false) {
    const response = await apiClient.post(`/knowledge-graph/build?clear_existing=${clearExisting}`);
    return response.data;
  }
};

export const dashboardApi = {
  async getCategories() {
    const response = await apiClient.get('/dashboard/categories');
    return response.data;
  },

  async runFullPdfSort() {
    const response = await apiClient.post('/dashboard/file-sorting/run-all');
    return response.data;
  },

  async updatePolicyCategory(filename, newCategory) {
    const response = await apiClient.post('/dashboard/policy-category', {
      filename,
      new_category: newCategory
    });
    return response.data;
  },

  async bulkUpdatePolicyCategories(filenames, newCategory) {
    const response = await apiClient.post('/dashboard/policy-category/bulk', {
      filenames,
      new_category: newCategory
    });
    return response.data;
  },

  async resetPolicyCategory(filename) {
    const response = await apiClient.delete(`/dashboard/policy-category/${encodeURIComponent(filename)}`);
    return response.data;
  },

  async getOldPDFs(years = 10) {
    const response = await apiClient.get('/dashboard/old-pdfs', { params: { years } });
    return response.data;
  },

  async getSimilarity(pdfName) {
    const response = await apiClient.get(`/dashboard/similarity/${encodeURIComponent(pdfName)}`);
    return response.data;
  },

  async getStaleness() {
    const response = await apiClient.get('/dashboard/policy-staleness');
    return response.data;
  }
};

export const authApi = {
  async login(username, password) {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    const response = await apiClient.post('/auth/login', formData.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },

  async register(username, password, email) {
    const response = await apiClient.post('/auth/register', {
      username,
      password,
      email: email || null,
      role: 'user',
    });
    return response.data;
  },

  async me() {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  async logout() {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },
};

export default apiClient;
